import Quill from 'quill';
import $ from 'jquery';
import CustomClipboard from './CustomClipboard';

import constants from '../constants';
import util from '../util';

import History from './history';
import Cached from './cached';
import Renderer from './renderer';

import store from '../store';
import * as actions from '../actions';
import { UppercaseAttributor } from './formats/uppercase.format';
import { BlockSizeAttributor } from './formats/block-size.format';
import { BlockFontAttributor } from './formats/block-font.format';
import { BlockLineHeightAttributor } from './formats/block-line-height.format';
import { LetterSpacingAttributor } from './formats/letter-spacing.format';
import config from '../config';
import { TextAlignAttributor } from './formats/text-align.format';
import {updateActiveFonts} from "./font";
import {markAsModified} from "./mark-as-modified";
import {setLineHeights, setParagraphLineHeight} from "./line-height";
import { updatePageBars } from './selection';
import Interactable from "./interactable";
import wordArt, { wrapToCurve } from './word-art';

const IS_INLINE_COLOR_ENABLED = window.data.props.featureInlineColor;
const IS_INLINE_HIGHLIGHTS_ENABLED = true;
let EDITABLES = {};

const TEXT_CACHE = {};
const INLINE_STYLES = ['fontSize', 'fontWeight', 'fontStyle', 'textDecoration', 'textTransform', 'fontFamily', 'letterSpacing',
        'lineHeight', 'textAlign'];

const INLINE_STYLES_CSS = ['font-size', 'font-weight', 'font-style', 'text-decoration', 'text-transform', 'font-family',
        'letter-spacing', 'line-height', 'text-align'];

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'case', 'blockSize', 'blockFont', 'letterSpacing', 'blockLineHeight',
        'blockTextAlign'];

const WORD_STYLES = ['fontWeight', 'fontStyle', 'textDecoration', 'textTransform', 'letterSpacing'];
const LINE_STYLES = ['fontSize', 'fontFamily', 'lineHeight', 'textAlign'];
const BLOCK_FORMATS = ['blockSize', 'blockFont', 'blockLineHeight', 'blockTextAlign'];
const WORD_BREAKERS = [' ', '\n'];

let quillWasEnabled = false;

if (IS_INLINE_COLOR_ENABLED) {
    const COLOR = 'color';
    INLINE_STYLES.push(COLOR);
    INLINE_STYLES_CSS.push(COLOR);
    QUILL_FORMATS.push(COLOR);
    WORD_STYLES.push(COLOR);
}

const zone = {
    isInGetInlineStyles: false,
};

const FontSizer = (function () {
    let CACHE = [];

    const get = (elem) => {
        const found = CACHE.find((it) => it.el === elem);
        return found ? found.size : '';
    };

    const cache = ($ps) => {
        CACHE = [];
        $ps.each(function () {
            const $p = $(this);
            CACHE.push({ el: $p[0], size: util.getCssRule($p, 'font-size') });
        });
    };

    const fix = ($elem) => {
        if (!$elem) return;
        const size = get($elem[0]);
        if (!util.getCssRule($elem, 'font-size') && size) {
            $elem.css('font-size', size);
        }
    };

    return {
        get,
        fix,
        cache,
    };
}());

const initQuillEnvironment = () => {
    Quill.register('modules/clipboard', CustomClipboard, true);
    Quill.register(UppercaseAttributor, true);
    Quill.register(TextAlignAttributor, true);
    Quill.register(BlockSizeAttributor, true);
    Quill.register(BlockFontAttributor, true);
    Quill.register(BlockLineHeightAttributor, true);
    Quill.register(LetterSpacingAttributor, true);
    if (config.debug) {
        Quill.debug('log');
    }
};

const getQuill = ($element) => EDITABLES[$element.attr('id')];

const removeQuill = ($element) => delete EDITABLES[$element.attr('id')];

const disableActiveEdit = ($element) => {
    const quill = getQuill($element);
    if (quill) quill.enable(false);
};

const enableQuill = ($element, enable) => {
    const quill = getQuill($element);
    quillWasEnabled = true;

    if (enable) {
        for (const key in EDITABLES) {
         if (EDITABLES.hasOwnProperty(key)) {
                const aux = EDITABLES[key];
                if (aux.isEnabled()) aux.enable(false);
            }
        }
    }
    if (quill && quill.isEnabled() !== enable) {
        quill.enable(enable);
    }

    quillWasEnabled = false;
};

const hasActiveQuill = ($element, state) => {
    const instance = getQuill($element);
    return instance && instance.isEnabled() && hasSelection($element, state);
};

let manualDeselection = false;
const deselectText = ($element) => {
    const instance = getQuill($element);
    if (!instance) return;

    manualDeselection = true;
    instance.setSelection(null);
};

const fixNewParagraphs = ($newParagraphs, $oldParagraphs, elLineHeight) => {
    if ($newParagraphs.length > $oldParagraphs.length) {
        $newParagraphs.last().remove();
    }

    $newParagraphs.each(function (idx) {
        $(this).css('line-height', util.getCssRule($oldParagraphs.eq(idx), 'line-height') || elLineHeight || '1em');
    });
};

const ensureFallBackElementColor = ($element) => {
    if (util.getCssRule($element, 'color')) return;

    const $kids = $element.find('p').children();
    let i;
    const { length } = $kids;

    let inlineColor = null;
    for (i = 0; i < length; i++) {
        const $kid = $kids[i];
        try {
            const color = $kid ? $kid.style.color : null;
            if (color) {
                inlineColor = color;
                break;
            }
        } catch (err) { }
    }

    if (inlineColor) {
        $element.css('color', inlineColor);
    }
};

const initQuill = ($element) => {
    const $txt = $element.find('.txt');

    if (!getQuill($element)) {
        ensureFallBackElementColor($element);

        const $oldParagraphs = $element.find('p');

        EDITABLES[$element.attr('id')] = new Quill($txt[0], {
            formats: QUILL_FORMATS,
        });

        let elLineHeight = util.getCssRule($element, 'line-height');
        const txtLineHeight = util.getCssRule($txt, 'line-height');
        if (txtLineHeight) {
            elLineHeight = txtLineHeight;
        }
        fixNewParagraphs($element.find('p'), $oldParagraphs, elLineHeight);

        const quill = EDITABLES[$element.attr('id')];
        quill.on('selection-change', onSelectionChange.bind(EDITABLES[$element.attr('id')]));
        quill.on('text-change', onTextChange.bind(EDITABLES[$element.attr('id')]));
        quill.root.addEventListener('blur', function(ev) {
            if ($(ev.relatedTarget).length > 0 ||
                !$(ev.target).hasClass('ql-editor') ||
                quillWasEnabled) {
                return;
            }
            quill.setSelection(null);
        });

        $txt.bind('DOMNodeInserted', (ev) => {
            if (!ev.target) return;
            const { target } = ev;
            if (target.tagName === 'P') {
                setParagraphLineHeight($(target));
            } else if (target.tagName === 'SPAN') {
                const parent = ev.target.parentNode;
                if (parent.tagName === 'P') {
                    FontSizer.fix($(target).parents('p'));
                    target.style.fontSize = '';
                }
            }
        });
    }
};

const restartQuill = ($element) => {
    enableQuill($element, false);
    enableQuill($element, true);

    const $txt = $element.find('.txt');
    $txt.focus();
};

const getBlockSizeFromNext = (instance, range) => {
    const after = range.index + range.length;
    const { ops } = instance.getContents();

    let i; let count = 0;
    const { length } = ops;

    for (i = 0; i <= length; i++) {
        const op = ops[i];
        if (op && op.insert) {
            count += op.insert.length;
        }
        if (count >= after) {
            const format = instance.getFormat(count, 0);
            if (format.blockSize) {
                return format.blockSize;
            }
        }
    }

    return '100%';
};

const onSelectionChange = function (range) {
    if (range) {
        store.getState().$element.addClass('text-in-focus');
    } else {
        $('[data-component="text"]').each(function() {
            $(this).removeClass('text-in-focus');

            /* possible implementation for changing width for curved text as you type
            const $element = store.getState().$element;
            const qlEditorFirstChild = $element.find('.ql-editor').children().get(0);
            const activeDims = util.getActiveDimensions();
            const computedLeft = wordArt.getStyles($element[0])['left'];
            const offsetLeft = computedLeft.includes('%') ? computedLeft : util.pixelToPercent(parseFloat(computedLeft), activeDims.width);
            const setWidth = Math.min(util.pixelToPercent(wordArt.getTextWidth(qlEditorFirstChild.innerText, wordArt.getCanvasFont(qlEditorFirstChild)) + 20, activeDims.width),
                                    100 - parseFloat(offsetLeft));
            $element.width(setWidth + '%');*/
        })
    }

    if (zone.isInGetInlineStyles) return;

    if (!this.isEnabled()) {
        if (manualDeselection) manualDeselection = false;
        return;
    }
    if (manualDeselection) {
        manualDeselection = false;
        return;
    }

    if (store.getState().isMultiSelect) {
        return;
    }

    if (document.activeElement && document.activeElement.classList.contains('change-size-input')) {
        return;
    }

    if (!range) range = { index: 0, length: 0 };

    let format = this.getFormat(range.index, range.length);
    format = getFullSelectionFormat.bind(this)(format, range);
    ensureFormatColor(format, range);

    if (format.blockSize === '') {
        format.blockSize = getBlockSizeFromNext(this, range);
    }

    if (couldBeCorrupted(this, range)) {
        const prev = this.getFormat(range.index - 2, 0);
        const next = this.getFormat(range.index + 2, 0);
        format = { ...format, ...prev, ...next };
        applyFormat(this, format, range);
    } else {
        Cached.setFormat(format);
    }

    store.dispatch(actions.setIsTextHighlight(store.getState().$element, range, format));
};


const onTextChange = function (delta, source) {
    if (store.getState().textWrapped) {
        delta.ops.forEach(op => {
            if (op['delete'] || op['insert']) {
                wrapToCurve(store.getState().$element);
            }
        })
    }
}

const getFallbackColor = (instance, range) => {
    if (range.index > 0 && range.length === 0 && util.isNewLineChar(instance.getText()[range.index + range.length])) {
        const back = instance.getFormat(range.index - 2, 1) || {};
        return back.color || constants.DEFAULT_TEXT_COLOR;
    }
    return constants.DEFAULT_TEXT_COLOR;
};

const ensureFormatColor = (format, range) => {
    if (!IS_INLINE_COLOR_ENABLED) return;
    if (!format.color) {
        format.color = getFallbackColor(this, range);
        try {
            const computed = window.getComputedStyle($(this.container).closest('.comp').get(0), null);
            format.color = util.rgb2hex(computed.getPropertyValue('color'));
        } catch (err) {
            console.log(err.stack);
        }
    }
};

const couldBeCorrupted = () => false;

const isCorruptFormat = (format) => Object.keys(format).length === 1
        && (IS_INLINE_COLOR_ENABLED
        && format.color === constants.DEFAULT_TEXT_COLOR);

const getFullSelectionFormat = function (format, range) {
    const first = this.getFormat(range.index, 1);
    return { ...first, ...format };
};

const ensureTextBlocks = ($element) => {
    const version = parseInt($element.attr('blk-v') || 0, 10);
    if (version < window.data.props.featureBlockedTextVersion) {
        $element.attr('blk-v', window.data.props.featureBlockedTextVersion);

        const $txt = $element.find('.txt');
        const html = $txt.html();
        const bits = html.indexOf('<br>') > -1 ? html.split('<br>') : [html];

        if (!bits[bits.length - 1]) {
            bits.pop();
        }

        let blocks = '';
        bits.forEach((bit) => {
            blocks += `<p>${bit || '&nbsp;'}</p>`;
        });

        $txt.html(blocks);
    }
};

const addTextToCache = ($element, text) => {
    TEXT_CACHE[$element.attr('id')] = text;
};

const startEditMode = ($element) => {
    if (manualDeselection === true) {
        manualDeselection = false;
    }

    ensureTextBlocks($element);
    initQuill($element);
    enableQuill($element, true);

    const $txt = $element.find('.txt');
    const $ql = $txt.find('.ql-editor');

    addTextToCache($element, $ql.html());
    FontSizer.cache($txt.find('p'));

    $txt.focus();
    Cached.setEditable($txt);
};

const isEmptyText = (txt) => txt.trim().length === 0 || (txt.length === 1 && txt.charCodeAt(0) === 8203);

const endEditMode = ($element) => {
    if (!getQuill($element)) return;

    //Interactable.of($element).setDragArea(true);

    enableQuill($element, false);
    deselectText($element);

    const $parent = $element.parent();
    const $txt = $element.find('.txt');
    let $ql = $txt.find('.ql-editor');
    Cached.clearEditable();

    const value = $ql.html();
    const oldValue = TEXT_CACHE[$txt.parents('[data-component="text"]').attr('id')];

    const txtValue = $ql.text();
    const oldTextValue = $(oldValue).text();

    const shouldRemove = isEmptyText(txtValue) && !util.isInGroup($element);

    if (oldValue !== value) markAsModified();

    setLineHeights($element);

    if (txtValue === oldTextValue || util.isGroup($element)) return;

    const undo = (cfg = {}) => {
        if (shouldRemove) {
            $parent.append($element);
            initQuill($element);
            $ql = $txt.find('.ql-editor');
        }
        $ql.html(oldValue);
        setLineHeights($element);
        if (util.isInGroup($element)) {
            Renderer.ensureGroupDimensions($element);
        }
        if (cfg.history) {
            TEXT_CACHE[$element.attr('id')] = oldValue;
        }
    };

    const redo = (cfg = {}) => {
        if (checkRemove()) return;

        $ql.html(value);
        setLineHeights($element);
        if (util.isInGroup($element)) {
            Renderer.ensureGroupDimensions($element);
        }
        if (cfg.history) {
            TEXT_CACHE[$element.attr('id')] = value;
        }
    };

    const checkRemove = () => {
        if (shouldRemove) {
            removeQuill($element);
            $element.remove();
        }
        return shouldRemove;
    };

    checkRemove();
    History.push(undo, redo, 'Update text value');
};

const selectAll = ($element) => {
    const instance = getQuill($element);
    instance.setSelection(0, instance.getText().length);
};

const selectEnd = ($element) => {
    const instance = getQuill($element);
    instance.setSelection(instance.getText().length, 0);
}

const getSelection = ($element) => {
    const quill = getQuill($element);
    return quill ? quill.getSelection() : null;
};

const hasSelection = ($element, state) => {
    const selection = getSelection($element);
    const selectionLength = selection ? selection.length : 0;
    return IS_INLINE_HIGHLIGHTS_ENABLED && (selectionLength > 0 || hasLostSelection($element, state));
};

const hasLostSelection = ($element, state) => util.isText($element) && (state || store.getState()).selectionLength > 0;

const isInlineStyle = (key) => INLINE_STYLES.indexOf(key) > -1;

const restoreSelection = ($element) => {
    const state = store.getState();
    const current = getQuill($element);
    if (!current) return;

    current.setSelection(0, 0);
    current.setSelection(state.selectionIndex, state.selectionLength);

    util.nextTick(() => enableQuill($element, true));
};

const applyInlineStyle = ($element, key, val, cfg = {}, oldScale) => {
    const $ql = $element.find('.ql-editor');
    const oldHtml = $ql.html();

    const selection = getSelection($element);
    if ((!selection || selection.length === 0) && val === constants.DEFAULT_TEXT_COLOR) return;

    if (!store.getState().isMobileVersion && (!selection || hasLostSelection($element)) && cfg.restoreSelection !== false) {
        restoreSelection($element);
    }

    format($element, key, val);
    const newHtml = $ql.html();

    const update = ($element, key, html, alterHtml = true, oldScale) => {
        addTextToCache($element, html);
        if (key === 'fontFamily') {
            updateActiveFonts();
        }

        if (alterHtml) {
            $ql.html(html);
        }

        if (key === 'fontSize') {
            FontSizer.cache($element.find('p'));

            if (!util.isLocked($element)) {
                Interactable.of($element).handleFontSizeChange(oldScale);
            }
        }
    };

    const undo = () => { update($element, key, oldHtml, true, oldScale); };
    const redo = (alterHtml = true) => { update($element, key, newHtml, alterHtml, 1); };

    History.push(undo, redo, 'Inline style');
    redo(false);
};

const fullFormat = ($element, format) => {
    const instance = getQuill($element);
    if (!instance) return;

    instance.setSelection(0, instance.getText().length);
    INLINE_STYLES.forEach((key) => $element.css(key, ''));

    $element.find('.ql-container').css('text-transform', '');
    applyFormat(instance, format);

    $element.css('line-height', '');
    const $txt = $element.find('.txt');
    $txt.css('line-height', '');
    $txt.find('p').css('line-height', format.lineHeight);

    $element.attr('inl', window.data.props.featureInlineVersion);
    instance.setSelection(0, 0);
};

const applyFormat = (instance, format, range = {}) => {
    const length = range.length || instance.getText().length;
    const index = range.index || 0;
    Object.keys(format).forEach((key) => {
        if (util.isSafari() || util.isEdge()) {
            instance.formatLine(index, length, key, format[key]);
            instance.formatText(index, length, key, format[key]);
        } else if (util.isFirefox() && IS_INLINE_COLOR_ENABLED && key === 'color') {
                instance.formatText(index, length, key, format[key]);
            } else {
                instance.format(key, format[key]);
            }
    });
};

const format = ($element, key, val) => {
    const instance = getQuill($element);
    if (!instance) return;
    const set = getInlineRuleSet($element, key, val);

    let selection = getSelection($element);
    const hasSelection = selection && selection.length > 0;

    if (!hasSelection) {
        selection = { index: 0, length: instance.getText().length };
    } else if (isLineStyle(key)) {
        selection = getBlockSelection(instance.getText(), selection.index, selection.length, false);
    } else if (isWordStyle(key)) {
        selection = getBlockSelection(instance.getText(), selection.index, selection.length);
    }

    if (set[0] === 'size') {
        set[1] = getInlineSize($element, set[1]);
    }

    if (BLOCK_FORMATS.indexOf(set[0]) > -1 && hasSelection) {
        instance.format(set[0], set[1]);
    } else {
        instance.formatText(selection.index, selection.length, set[0], set[1]);
    }
};

const getBlockSelection = (text, index, length, withSpace = true) => {
    let aux = index;
    if (WORD_BREAKERS.indexOf(text[index]) > -1) aux += 1;

    const first = text.substring(0, aux);
    const firstBit = util.getPrevWordBreak(first, withSpace);

    aux = index + length;
    if (WORD_BREAKERS.indexOf(text[aux - 1]) > -1) aux -= 1;

    const last = text.substring(aux);
    const lastBit = util.getNextWordBreak(last, withSpace);

    return {
        index: index - firstBit,
        length: length + firstBit + lastBit,
    };
};

const getInlineSize = ($element, value) => {
    const pageValue = parseFloat($element.parents('.page').css('font-size'));
    const parentValue = parseFloat($element.css('font-size'));
    const elementPercent = parseFloat(value);

    const elementValue = Math.ceil(pageValue * elementPercent) / 100;

    return `${parseInt(elementValue * 100 / parentValue, 10)}%`;
};

const removeInlineStyling = ($element, key) => {
    const instance = getQuill($element);
    const contents = instance.getContents();

    key = getInlineRuleKey(key);
    if (!key) return;

    contents.ops.forEach((it) => {
        if (it.attributes && it.attributes.hasOwnProperty(key)) {
            delete it.attributes[key];
        }
    });
    instance.setContents(contents);
};

const getInlineRuleSet = ($element, key, val) => {
    switch (key) {
        case 'color': return [key, val];
        case 'fontSize': return ['blockSize', val];
        case 'fontFamily': return ['blockFont', val];
        case 'fontWeight': return ['bold', val === 'bold'];
        case 'fontStyle': return ['italic', val === 'italic'];
        case 'textDecoration': return ['underline', val === 'underline'];
        case 'textTransform': return ['case', val === 'uppercase' ? 'up' : 'lw'];
        case 'textAlign': return ['blockTextAlign', val];
        case 'letterSpacing': return ['letterSpacing', val];
        case 'lineHeight': return ['blockLineHeight', val];
        default: return [key, val];
    }
};

const getInlineRuleKey = (key) => {
    switch (key) {
        case 'color': return 'color';
        case 'fontSize': return 'blockSize';
        case 'fontFamily': return 'blockFont';
        case 'fontWeight': return 'bold';
        case 'fontStyle': return 'italic';
        case 'textDecoration': return 'underline';
        case 'textTransform': return 'case';
        case 'textAlign': return 'blockTextAlign';
        case 'letterSpacing': return 'letterSpacing';
        case 'lineHeight': return 'blockLineHeight';
        default: return key;
    }
};

const isSameTextClick = ($this) => {
    const $element = $this.closest('[data-component]');
    if ($element.length === 0 || !util.isText($element)) return false;
    return util.isSelected($element);
};

function getElementBottomBorder($element) {
    const border = $element.css('border-bottom');
    if (border.indexOf('px') === -1) {
        return 0;
    }
    const [btm] = border.split('px');
    return parseInt(btm);
}

function ensureTextSize($element) {
    if (util.isInGroup($element)) return;

    const $txt = $element.find('.txt');
    let txtHeight = $txt.height() + getElementBottomBorder($element);
    let txtWidth = $txt.width();
    if (util.isWrappedText($element)) {
        const bBox = $element.find('#svgWrap>svg>text')[0].getBBox()
        txtHeight = bBox.height;
        txtWidth = bBox.width;
    }
    const elHeight = $element.height();

    const dimensions = util.getActiveDimensions();

    Interactable.of($element).savePositionInfo();

    if (elHeight === 0 && txtHeight === 0) {
        $element.css('height', 'auto');
    } else if (txtHeight > elHeight) {
        $element.css('height', `${util.pixelToPercent(txtHeight, dimensions.height)}%`);
    } else {
        $element.css('height', `${util.pixelToPercent(txtHeight, dimensions.height)}%`);
    }
    if (txtWidth > $txt.width()) {
        $element.css('width', `${util.pixelToPercent(txtWidth, dimensions.width)}%`);
    }

    const qlStyle = $element.find('.ql-container').attr('style');
    if (qlStyle) {
        let newStyle = '';
        qlStyle.split(';').forEach(s => {
            if (s.trim() && s.indexOf('width:') > -1) {
                newStyle += 'width: 100%;';
            } else if (s.trim() && s.indexOf('height:') < 0) {
                newStyle += s.trim() + ';';
            }
        });
        if (newStyle) {
            $element.find('.ql-container').attr('style', newStyle);
        } else {
            $element.find('.ql-container').removeAttr('style');
        }
    }

    fixHeightAfterResize($element);
}

const fixHeightAfterResize = ($element) => {
    if ($element) {
        // const qlEditor = $element.find('.ql-editor');
        // if (qlEditor.length) {
        //     const scale = Interactable.of($element).getScale();
        //     const height = qlEditor.height() * scale;
        //     const width = qlEditor.width() * scale;
        //     qlEditor.parents('.ql-container').css({
        //         'height': `${height}px`,
        //         'width': `${width}px`
        //     });
        //     $element.css({
        //         'height': `${height}px`,
        //         'width': `${width}px`
        //     });
        //
        // }
        // const $ps = $element.find('.ql-container p');
        // if ($ps.length) {
        //     const $container = $element.find('.ql-container');
        //     const scale = Interactable.of($element).getScale();
        //
        //     console.log(scale);
        //
        //     let height = 0;
        //     let width = $ps.outerWidth() * scale;
        //
        //     $ps.each(function () {
        //         const $p = $(this);
        //         height += $p.outerHeight() * scale;
        //     });
        //
        //     $container.css({
        //         'height': `${height}px`,
        //     });
        //
        //     $element.css({
        //         'height': `${height}px`,
        //         'width': `${width}px`
        //     });
        //     // $container.css({
        //     //     'height': `${height / scale}px`
        //     // });
        // }
    }
}

const moveCursorToEnd = ($element) => {
    const quill = getQuill($element);
    quill.setSelection(quill.getSelection().index + 2, Quill.sources.SILENT);
};

const manualEnterEvent = (ev) => {
    ev.preventDefault();
    const $target = $(ev.target);
    const $editor = $target.find('.ql-editor');
    if ($editor.length > 0) {
        const $element = $target.parents('[data-component="text"]');
        const quill = getQuill($element);
        const selection = quill.getSelection();
        quill.insertText(selection.index, '\n');
    }
};

const isWordStyle = (key) => WORD_STYLES.indexOf(key) > -1;

const isLineStyle = (key) => LINE_STYLES.indexOf(key) > -1;

const getFirstStyles = ($element) => {
    const instance = getQuill($element);
    if (!instance) return {};

    let attrs = {};
    const { ops } = instance.getContents();
    let i = 0;
    const { length } = ops;

    while (i < length && !attrs.blockSize && !attrs.blockFont) {
        if (ops[i].attributes) {
            attrs = { ...attrs, ...ops[i].attributes };
        }
        i++;
    }

    return attrs || {};
};

const getInlineStyles = ($element, state, isSameElementSelected) => {
    zone.isInGetInlineStyles = true;

    const hasActiveStyles = hasActiveQuill($element, state);
    const firstQuillStyles = !hasActiveStyles ? getFirstStyles($element) : {};

    let { color } = state;
    if (IS_INLINE_COLOR_ENABLED) {
        if (!isSameElementSelected) {
            color = (hasActiveStyles ? $element.css('color') : firstQuillStyles.color) || $element.css('color') || constants.DEFAULT_TEXT_COLOR;
            if (color.indexOf('rgb') > -1) color = util.rgb2hex(color);
        }
    } else {
        color = $element.css('color');
        if (color.indexOf('rgb') > -1) color = util.rgb2hex(color);
    }

    let { fontFamily } = state;
    let { fontFamilyKey } = state;
    if (!isSameElementSelected) {
        const family = hasActiveStyles ? $element.css('font-family') : firstQuillStyles.blockFont;
        const font = util.getFontByFamily(util.cleanFont(family));
        fontFamily = font ? font.label : '';
        fontFamilyKey = font ? font.key : '';
    }

    const isTextHighlight = isSameElementSelected ? state.isTextHighlight : false;
    const selectionIndex = isSameElementSelected ? state.selectionIndex : 0;
    const selectionLength = isSameElementSelected ? state.selectionLength : 0;

    const fontWeight = $element.css('font-weight') || '#FFF';
    const fontStyle = $element.css('font-style');
    const textDecoration = $element.css('text-decoration').indexOf('underline') > -1 ? 'underline' : 'none';
    const textTransform = $element.css('text-transform').indexOf('uppercase') > -1 ? 'uppercase' : 'none';
    const lineHeightRule = (util.getComponentLineHeight($element) || 1) * 10;
    const textAlignRule = $element.css('text-align') || 'center';

    let fontSize = isSameElementSelected ? state.fontSize : 0;
    const letterSpacing = isSameElementSelected ? state.letterSpacing : util.formatLetterSpacing(firstQuillStyles.letterSpacing);
    let { isBold } = state;
    let { isItalic } = state;
    let { isUnderline } = state;
    let { isUppercase } = state;
    let { lineHeight } = state;
    let { textAlign } = state;

    if (!isSameElementSelected) {
        isBold = hasActiveStyles ? fontWeight === 'bold' || fontWeight > 500 : !!firstQuillStyles.bold;
        isItalic = hasActiveStyles ? fontStyle === 'italic' : !!firstQuillStyles.italic;
        isUnderline = hasActiveStyles ? textDecoration === 'underline' : !!firstQuillStyles.underline;
        isUppercase = hasActiveStyles ? textTransform === 'uppercase' : firstQuillStyles.case === 'up';
        fontSize = util.convertInlineSize(firstQuillStyles.blockSize) || 0;
        lineHeight = lineHeightRule;
        textAlign = hasActiveStyles ? textAlignRule : firstQuillStyles.blockTextAlign;
    }

    zone.isInGetInlineStyles = false;
    return {
        isTextHighlight,
        isBold,
        isItalic,
        isUnderline,
        isUppercase,
        fontFamily,
        fontFamilyKey,
        color,
        fontSize,
        selectionIndex,
        selectionLength,
        letterSpacing,
        lineHeight,
        textAlign,
    };
};

const isInlineStyled = ($element) => {
    if ($element.attr('inl')) return true;

    let elementHasInlineStyles = false;
    const elementStyle = $element.attr('style');

    INLINE_STYLES_CSS.forEach((key) => {
        if (elementStyle.indexOf(key) > -1) {
            elementHasInlineStyles = true;
        }
    });

    let hasStyledPs = false;
    if (!elementHasInlineStyles) {
        $element.find('p').each(function () {
            const pStyle = $(this).attr('style');
            INLINE_STYLES_CSS.forEach((key) => {
                if (pStyle.indexOf(key) > -1) {
                    hasStyledPs = true;
                }
            });
        });
    }

    return !elementHasInlineStyles && hasStyledPs;
};

const getTransferableInlineStyles = ($element) => {
    const styles = {};
    INLINE_STYLES_CSS.forEach((key) => {
        const val = util.getCssRule($element, key);
        if (val) {
            const rule = getInlineRuleSet($element, util.toCamelCase(key), val);
            if (rule[0] === 'blockFont') {
                styles[rule[0]] = rule[1].indexOf(',') > -1 ? rule[1].split(',')[0] : rule[1];
            } else {
                styles[rule[0]] = rule[1];
            }
        }
    });

    const $txt = $element.find('.txt');
    let lineHeight = util.getCssRule($txt, 'line-height');
    if (!lineHeight) {
        lineHeight = util.getCssRule($element, 'line-height') || '1em';
    }
    styles.lineHeight = lineHeight;

    return styles;
};

const enableInlineStyling = ($element) => {
    if (!isInlineStyled($element)) {
        const styles = getTransferableInlineStyles($element);
        fullFormat($element, styles);
    }
};

const sanitizeParagraph = ($paragraph, $page, cacheFontSize = false) => {
    const fontFamily = util.getCssRule($paragraph, 'font-family');

    let lineHeight = util.getCssRule($paragraph, 'line-height') || '1em';
    if (lineHeight.indexOf('px') > -1) lineHeight = '1em';

    const height = util.getCssRule($paragraph, 'height');

    let fontSize = util.getCssRule($paragraph, 'font-size') || '180%';
    if (fontSize.indexOf('px') > -1) {
        const pageSize = $page.css('font-size');
        fontSize = `${Math.round(100 * parseFloat(fontSize) / parseFloat(pageSize))}%`;
    }

    $paragraph.attr('style', '');

    $paragraph.css('fontSize', fontSize);
    $paragraph.css('fontFamily', fontFamily);
    $paragraph.css('lineHeight', lineHeight);
    $paragraph.css('height', height);

    if (cacheFontSize) {
        $paragraph.attr('data-font-size', fontSize);
    }

    return $paragraph;
};

const setCursorToTheEnd = (el) => {
    el.focus();
    if (typeof window.getSelection !== 'undefined'
        && typeof document.createRange !== 'undefined') {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    } else if (typeof document.body.createTextRange !== 'undefined') {
        const textRange = document.body.createTextRange();
        textRange.moveToElementText(el);
        textRange.collapse(false);
        textRange.select();
    }
};

const fixTextLineFormatting = ($theP) => {
    const theP = $theP.get(0);

    theP.style.fontFamily = store.getState().lastValidSelection.fontFamily;

    const dims = Renderer.getDimensions();
    if (dims.unit === 'in') {
        const pixels = util.getPointsInPixels(store.getState().lastValidSelection.fontSize, dims);
        const percent = util.pixelToPercent(pixels, dims.fontSize);
        theP.style.fontSize = `${percent}%`;
    } else {
        const percent = store.getState().lastValidSelection.fontSize * constants.PIXEL_FONT_SIZE_ADJUSTMENT;
        theP.style.fontSize = `${percent}%`;
    }
};

const fixTextBlockFormatting = (isUnderline, isItalic, isBold, lastUsedColor, configuration) => {
    let $wrapped = null;
    let core = null;

    if (isUnderline) {
        $wrapped = $('<u/>');
        $wrapped.text(configuration.text);
        core = $wrapped.get(0);
    }

    if (isItalic) {
        const $italic = $('<em/>');
        if ($wrapped === null) {
            $italic.text(configuration.text);
            core = $italic.get(0);
        } else {
            $italic.append($wrapped);
        }
        $wrapped = $italic;
    }

    if (isBold) {
        const $strong = $('<strong/>');
        if ($wrapped === null) {
            $strong.text(configuration.text);
            core = $strong.get(0);
        } else {
            $strong.append($wrapped);
        }
        $wrapped = $strong;
    }

    if (IS_INLINE_COLOR_ENABLED) {
        if (lastUsedColor) {
            if ($wrapped === null) {
                $wrapped = $('<span/>');
                $wrapped.text(configuration.text);
                core = $wrapped.get(0);
            }
            $wrapped.css('color', lastUsedColor);
        }
    }

    return { wrapped: $wrapped, core };
};

const fixParagraphsWithLostFormatting = (ev, $element) => {
    if (store.getState().isCtrlPressed === true || ev.keyCode === constants.SHIFT_KEY) {
        return true;
    }

    if (util.isText($element)) {
        const $text = $element.find('.ql-editor').first();
        const pToFix = [];

        let isUnderline = false; let isBold = false; let isItalic = false; let lastUsedColor = null;
        let hasFormatting = false;
        const isTripleClick = $text.data('is-triple-click');
        $text.removeData('is-triple-click');

        if (store.getState().lastValidSelection) {
            isUnderline = store.getState().lastValidSelection.isUnderline === true;
            isBold = store.getState().lastValidSelection.isBold === true;
            isItalic = store.getState().lastValidSelection.isItalic === true;
            lastUsedColor = store.getState().lastValidSelection.color;
            if (!!lastUsedColor || isUnderline || isBold || isItalic) {
                hasFormatting = true;
            }
        }

        let $currentLineP = null;
        if (IS_INLINE_COLOR_ENABLED) {
            try {
                const selection = window.getSelection();
                const range = selection.getRangeAt(0);
                $currentLineP = $(range.startContainer.parentNode).closest('p');

                // check for color lost
                if (!!lastUsedColor && $currentLineP.children().length > 0 && $currentLineP.children()[0].style.color === '') {
                    $currentLineP.children()[0].style.color = lastUsedColor;
                }
            } catch (err) {
                console.log(err.stack);
            }
        }

        if (IS_INLINE_COLOR_ENABLED) {
            // ISSUE[Chrome_Jumpy] : We'll put the saved paragraph on key down next to the problematic paragraph, and
            // remove the affected span that is antempted to be merged from line 1
            // if there are paragraphs with no font family attached to them, but there are root elements inside that
            // have different font families there elements must but put on their own lines
            if ($currentLineP && $currentLineP.children().length > 1) {
                if ($text.data('force-line-insertion')) {
                    $currentLineP.children().each(function (idx) {
                        if (idx > 0) {
                            $(this).remove();
                            if ($text.data('force-line-insertion')) {
                                $text.data('force-line-insertion').insertAfter($currentLineP);
                                $text.removeData('force-line-insertion');
                            }
                        }
                    });
                }
            }
        }

        $text.find('p').each(function () {
            const $currentP = $(this);
            if (IS_INLINE_COLOR_ENABLED) {
                if ($currentP.children().length === 1 && $currentP.children()[0].tagName.toLowerCase() === 'br' && hasFormatting) {
                    const $div = $('<div/>');
                    const fixedTextBlockFormatting = fixTextBlockFormatting(isUnderline, isItalic, isBold, lastUsedColor, '');
                    const $wrapped = fixedTextBlockFormatting.wrapped;
                    const { core } = fixedTextBlockFormatting;
                    $(core).html('&#8203;');
                    $div.append($wrapped);

                    $currentP.html($div.html());
                }
            }

            // if CTRL+A was pressed and then the user types something, in FF all the html content in the content editable
            // div will be lost and also the formatting attached to it, including the font familly
            // if there is no font family for the current line (p) then the line has to be corrected
            if (util.isFirefox() || !isTripleClick) {
                if ($currentP.get(0).style.fontFamily === '' || $currentP.get(0).style.fontSize === '') {
                    pToFix.push($currentP);
                    return true;
                }
            }

            if (IS_INLINE_COLOR_ENABLED) {
                const hasNoChildren = $currentP.children().length === 0 && hasFormatting;
                // formatted lines have at least one child inside the representing "P" element. If the text was formatted
                // and now it has no child inside the "P" we need to correct it. This happens on triple click with very fast typing.
                if (hasNoChildren) {
                    pToFix.push($currentP);
                    return true;
                }
            }
        });

        if (pToFix.length > 0 && !!store.getState().lastValidSelection) {
            let core = null;
            for (let i = 0; i < pToFix.length; i++) {
                const $theP = pToFix[i];
                const text = $.trim($theP.text());

                $theP.text('');

                fixTextLineFormatting($theP);

                const fixedTextBlockFormatting = fixTextBlockFormatting(isUnderline, isItalic, isBold, lastUsedColor, { text });
                const $wrapped = fixedTextBlockFormatting.wrapped;
                core = fixedTextBlockFormatting.core;

                if ($wrapped !== null) {
                    $theP.append($wrapped);
                } else {
                    $theP.text(text);
                    core = $theP.get(0);
                }
            }

            if (core !== null) {
                $element.click();
                setCursorToTheEnd(core);
            }
        }
    }
};

const fixLineHeight = ($element, defaultLineHeight = 1) => {
    $element.css('height', 'auto');

    const lineHeight = parseFloat($element.css('line-height')) || 1;

    const style = util.getCssRule($element, 'line-height');
    const pLineHeight = style ? parseFloat(style) : defaultLineHeight;

    let height = $element.height();
    if (height === 0) height = lineHeight;

    const heightRule = `${Math.round(height / lineHeight) * pLineHeight}em`;
    $element.css({
        height: heightRule,
    });

    const $target = $element.closest('[data-component="text"]');
    fixHeightAfterResize($target);
    updatePageBars($target);
};

initQuillEnvironment();

const clearQuills = () => EDITABLES = {};

function spellCheckText(key, value) {
    $('[data-component="text"]').each(function() {
        const $this = $(this);
        const txt = $this.html();
        if (txt.indexOf(key) > -1) {
            $this.html(txt.replaceAll(key, value));
            enableQuill($this, false);
            enableQuill($this, true);
        }
    })
}

export default {
    clearQuills,
    getQuill,
    removeQuill,
    initQuill,
    restartQuill,

    startEditMode,
    endEditMode,
    ensureTextBlocks,

    getSelection,
    hasSelection,
    selectAll,
    selectEnd,

    isInlineStyle,
    applyInlineStyle,

    isSameTextClick,
    ensureTextSize,
    disableActiveEdit,

    manualEnterEvent,
    moveCursorToEnd,

    removeInlineStyling,
    restoreSelection,
    getInlineStyles,

    enableInlineStyling,
    sanitizeParagraph,

    isCorruptFormat,

    setCursorToTheEnd,
    fixParagraphsWithLostFormatting,
    manualDeselection,

    fixLineHeight,
    fixHeightAfterResize,

    spellCheckText
};
